import React from "react";
import { motion } from "framer-motion";
import { Heart, Baby } from "lucide-react";
import { useGoTo } from "../nav";
import { useLang } from "../i18n";
import logoImg from "../assets/logo.png";
import real2 from "../assets/real-2.jpg";
import real6 from "../assets/real-6.jpg";
import real9 from "../assets/real-9.jpg";
import { Cloud, Sparkle, Planet, Rocket, PhotoCard, Flower, Rainbow, Sun, Butterfly } from "./decor";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: 0.12 * i, duration: 0.6, ease: "easeOut" } }),
};

export default function Hero() {
  const goTo = useGoTo();
  const { s } = useLang();
  return (
    <section id="home" data-testid="hero-section" className="relative hero-grid overflow-hidden pt-32 sm:pt-44 pb-8">
      {/* ambient decorations */}
      <Cloud className="absolute left-[4%] top-28 w-20 sm:w-28 opacity-90 animate-drift" />
      <Cloud className="absolute right-[5%] top-24 w-16 sm:w-24 opacity-80 animate-floaty" />
      <div className="absolute left-[12%] top-52 hidden md:block animate-floaty">
        <div className="animate-spin" style={{ animationDuration: "16s" }}>
          <Sun className="w-12 h-12" />
        </div>
      </div>
      <Sparkle className="absolute left-[9%] top-40 w-7 hidden md:block" color="#E08D75" opacity="0.6" />
      <Planet className="absolute right-[10%] top-48 w-10 hidden md:block" color="#F8DFD6" />
      <Rocket className="absolute right-[7%] top-36 w-12 hidden md:block" />
      <Butterfly className="absolute right-[13%] top-64 w-9 hidden sm:block animate-floaty" />
      <Heart className="absolute left-[16%] top-[46%] w-5 h-5 fill-[#F9A8D4] text-[#F472B6] hidden md:block animate-floaty" />
      <Heart
        className="absolute right-[18%] top-[58%] w-4 h-4 fill-[#C4B5FD] text-[#A78BFA] hidden md:block animate-floaty"
        style={{ animationDelay: "1.4s" }}
      />
      {/* bouncing balls */}
      <div className="absolute left-[22%] bottom-[30%] w-5 h-5 rounded-full bg-[#FBD85A] shadow-soft hidden md:block animate-bounce" style={{ boxShadow: "inset -3px -5px 0 rgba(0,0,0,0.08)" }} />
      <div className="absolute right-[24%] bottom-[24%] w-4 h-4 rounded-full bg-[#8CE0C5] shadow-soft hidden md:block animate-bounce" style={{ animationDelay: "0.45s", boxShadow: "inset -3px -4px 0 rgba(0,0,0,0.08)" }} />

      <div className="relative max-w-4xl mx-auto px-4 text-center">
        {/* logo with bouncing rainbow + spinning sun */}
        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0} className="relative w-fit mx-auto">
          <Rainbow className="hidden sm:block absolute -left-28 sm:-left-32 top-2 w-24 sm:w-28 animate-bounce" style={{ animationDelay: "0.2s" }} />
          <div className="hidden sm:block absolute -right-28 sm:-right-32 top-0 animate-floaty">
            <div className="animate-spin" style={{ animationDuration: "16s" }}>
              <Sun className="w-16 h-16" />
            </div>
          </div>
          <Heart className="absolute -left-10 top-1/2 w-4 h-4 fill-[#F9A8D4] text-[#F472B6] animate-floaty" style={{ animationDelay: "0.8s" }} />
          <Heart className="absolute -right-10 top-1/3 w-4 h-4 fill-[#C4B5FD] text-[#A78BFA] animate-floaty" style={{ animationDelay: "1.8s" }} />
          <img
            data-testid="hero-logo"
            src={logoImg}
            alt="Neni’s Playhouse Childcare logo"
            className="w-40 sm:w-52 lg:w-60 rounded-full shadow-card mx-auto animate-floaty bg-white"
          />
        </motion.div>

        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={1} className="mt-6 flex justify-center">
          <span
            data-testid="hero-ages-pill"
            className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2 shadow-card font-display font-semibold text-forest text-base sm:text-lg animate-floaty"
          >
            <Baby className="w-5 h-5 text-coral" />
            {s.hero.ages}
          </span>
        </motion.div>

        <motion.h1
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={2}
          className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight mt-6"
        >
          <span className="text-sageBlue">{s.hero.t1}</span>
          <span className="block font-bold text-coral">{s.hero.t2}</span>
        </motion.h1>

        <motion.p
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={3}
          className="mt-5 text-base sm:text-lg text-forest/80 max-w-2xl mx-auto leading-relaxed"
        >
          {s.hero.sub}
        </motion.p>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={4}
          className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-5"
        >
          <a
            data-testid="hero-cta-schedule"
            href="/contact"
            onClick={(e) => {
              e.preventDefault();
              goTo("/contact");
            }}
            className="inline-flex items-center gap-2 rounded-full bg-brown text-white font-display font-medium text-lg px-8 py-3.5 shadow-card hover:bg-[#4a338f] hover:scale-[1.03] transition-all duration-200"
          >
            {s.hero.cta1}
            <svg viewBox="0 0 20 20" className="w-4 h-4" fill="none" aria-hidden="true">
              <path d="M7 5l5 5-5 5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
          <a
            data-testid="hero-cta-programs"
            href="#programs"
            onClick={(e) => {
              e.preventDefault();
              goTo("#programs");
            }}
            className="font-display font-semibold text-forest underline underline-offset-4 decoration-2 hover:text-coral transition-colors"
          >
            {s.hero.cta2}
          </a>
        </motion.div>
      </div>

      {/* full-view photo cards */}
      <div className="relative max-w-5xl mx-auto px-6 mt-14 flex flex-wrap items-end justify-center gap-10 sm:gap-16 lg:gap-24">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="relative -rotate-2"
        >
          <PhotoCard
            testid="hero-photo-left"
            src={real2}
            alt="Inside our playroom at Neni’s Playhouse Childcare"
            color="#F9C6E3"
            className="w-36 sm:w-48 lg:w-60"
          />
        </motion.div>

        <Flower className="hidden sm:block absolute left-[30%] bottom-20 w-8 animate-sway" petal="#F58FBE" />

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, delay: 0.3, ease: "easeOut" }}
          className="relative rotate-1"
        >
          <PhotoCard
            testid="hero-photo-center"
            src={real9}
            alt="Friends building with blocks under the WELCOME banner and rainbow curtains"
            color="#D9F3E3"
            className="w-36 sm:w-48 lg:w-60"
          />
        </motion.div>

        <Flower className="hidden lg:block absolute right-[29%] bottom-32 w-6" petal="#8CE0C5" />

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, delay: 0.15, ease: "easeOut" }}
          className="relative rotate-2"
        >
          <PhotoCard
            testid="hero-photo-right"
            src={real6}
            alt="Our circle-time and play area"
            color="#CDEBFB"
            className="w-36 sm:w-48 lg:w-60"
          />
        </motion.div>
      </div>
    </section>
  );
}
